import { Tag } from '@apis/Resources/model';
import { Card, Group, Space, Text } from '@mantine/core';
import { AnchorButton } from '@root/Design/Primitives';
import { useDi } from '@root/Services/DI';
import { ResourceService } from '@root/Services/Resources/ResourceService';
import { useEffect, useState } from 'react';
import { CirclePlus } from 'tabler-icons-react';
import { ResourceTagService } from '../Services/ResourceTagService';
import { BulkTagProps } from './BulkTagProps';
import { TagEditor } from './TagEditor';
import { TagField } from './TagField';
import { CreateLabelKey, validateLabelKey, validateLabelValue } from '@root/Components/Resources/Gcp/LabelUtils';

export const AddLabels = (props: BulkTagProps) => {
    const [labelKeyList, setLabelKeyList] = useState<string[]>([]);
    const [labelValueList, setLabelValueList] = useState<string[]>([]);
    const [hybridValueList, setHybridValueList] = useState<string[]>([]);
    const resourceSvc = useDi(ResourceService);
    const tagSvc = useDi(ResourceTagService);
    const [disableSubmit, setDisableSubmit] = useState<boolean>(true);
    const [canRemove, setCanRemove] = useState<boolean>(false);
    const [labels, setLabels] = useState<Tag[]>([{ Key: '', Value: '' }]);
    const [overwriteConflicts, setOverwriteConflicts] = useState<boolean>(true);
    const [labelErrors, setLabelErrors] = useState<Map<number, { keyError?: string; valueError?: string }>>(new Map());

    const getAllKnownValues = async () => {
        let allKeys = (await resourceSvc.getTags()).sort();
        setHybridValueList([...allKeys]);
        return allKeys;
    };

    useEffect(() => {
        (async () => {
            setLabelKeyList((await getAllKnownValues()).sort());
            setLabelValueList((await resourceSvc.getAllTagValues()).sort());
        })();

        if (props.tagKeyGettingValue) {
            setLabelKey(0, props.tagKeyGettingValue);
        }
    }, []);

    useEffect(() => {
        const validForm = formIsValid();
        setDisableSubmit(!validForm);
        setCanRemove(labels.length > 1);

        const newErrors = new Map<number, { keyError?: string; valueError?: string }>();
        labels.forEach((label, index) => {
            const keyError = label.Key ? validateLabelKey(label.Key) : undefined;
            const valueError = label.Value ? validateLabelValue(label.Value) : undefined;
            if (keyError || valueError) {
                newErrors.set(index, {
                    keyError: keyError || undefined,
                    valueError: valueError || undefined,
                });
            }
        });
        setLabelErrors(newErrors);
    }, [JSON.stringify(labels)]);

    const formIsValid = () => {
        if (labels.some((l) => !l.Key || !l.Value)) {
            return false;
        }

        if (labels.some((l) => validateLabelKey(l.Key ?? '') !== null || validateLabelValue(l.Value ?? '') !== null)) {
            return false;
        }

        return true;
    };

    const addRowHandler = () => {
        setLabels((prevState) => {
            let newState = [...prevState];
            newState.push({ Key: '', Value: '' });
            return newState;
        });
    };

    const removeRowHandler = (index: number) => {
        if (labels.length > 1) {
            setLabels((prevState) => {
                let newState = [...prevState];
                newState.splice(index, 1);
                return newState;
            });
        }
    };

    const setLabelKey = (index: number, key: string) => {
        setLabels((prevState) => {
            let newState = [...prevState];
            let label = newState[index];
            label.Key = key;
            newState.splice(index, 1, label);
            return newState;
        });
    };

    const setLabelValue = (index: number, value: string) => {
        setLabels((prevState) => {
            let newState = [...prevState];
            let label = newState[index];
            label.Value = value;
            newState.splice(index, 1, label);
            return newState;
        });
    };

    const handleValueOnFocus = async (index: number = 0) => {
        if (labels[index]?.Key && labelKeyList.includes(labels[index]?.Key!)) {
            const fullKey = CreateLabelKey(labels[index].Key!);
            setHybridValueList((await resourceSvc.getTagValues(fullKey)).sort());
        } else {
            setHybridValueList([...labelValueList]);
        }
    };

    const labelsWithPrefix = labels.map((l) => ({
        Key: CreateLabelKey(l.Key!),
        Value: l.Value,
    }));

    const getKeyError = (index: number = 0) => {
        if (labelErrors.has(index) && labelErrors.get(index)?.keyError) {
            return labelErrors.get(index)?.keyError;
        }
        return undefined;
    };

    const getValueError = (index: number = 0) => {
        if (labelErrors.has(index) && labelErrors.get(index)?.valueError) {
            return labelErrors.get(index)?.valueError;
        }
        return undefined;
    };

    const tagJob = tagSvc.createTagJob(props.resourceIds, { AddTags: labelsWithPrefix, OverwriteConflicts: overwriteConflicts });

    return (
        <TagEditor
            onButtonClick={props.onButtonClick}
            companyId={props.companyId}
            resourceIds={props.resourceIds}
            pageTitle={props.pageTitle}
            instructions={props.instructions}
            canSubmit={!disableSubmit}
            setDisableSubmit={setDisableSubmit}
            overwriteConflicts={!overwriteConflicts}
            setOverwriteConflicts={setOverwriteConflicts}
            operationType="add"
            tagJob={tagJob}
            onTagJobStarting={props.onTagJobStarting}
            onTagJobStarted={props.onTagJobStarted}
        >
            {labels.map((label, index) => (
                <>
                    <Card withBorder radius="md" p="sm">
                        <Group noWrap grow key={`group${index}`} spacing={0} mb="xs">
                            <TagField
                                children={
                                    <>
                                        <TagField
                                            atid={'LabelKeyInput:' + index.toString()}
                                            key={`AddLabelKey${index}`}
                                            fieldValue={label.Key ?? ''}
                                            index={index}
                                            count={labels.length}
                                            valuePickList={labelKeyList}
                                            label="Key"
                                            setSelectedValue={setLabelKey}
                                            canAdd={false}
                                            canRemove={false}
                                            receiveInitalFocus={true}
                                            type="key"
                                            error={getKeyError(index)}
                                        />
                                    </>
                                }
                                atid={'LabelValueInput:' + index}
                                key={`AddLabelValue${index}`}
                                fieldValue={label.Value ?? ''}
                                index={index}
                                count={labels.length}
                                valuePickList={hybridValueList}
                                label="Value"
                                setSelectedValue={setLabelValue}
                                handleAddRow={addRowHandler}
                                handleRemoveRow={removeRowHandler}
                                canAdd={true}
                                canRemove={canRemove}
                                onFocus={handleValueOnFocus}
                                error={getValueError(index)}
                            />
                        </Group>
                    </Card>
                    <Space h="sm" />
                </>
            ))}
            <Card withBorder radius="md" p="sm">
                <AnchorButton atid="AddLabelsAddAnchor" text="Add" onClick={addRowHandler} icon={<CirclePlus size={20} />} />
            </Card>
        </TagEditor>
    );
};
